import type { RoomId } from '../ids';

// 자리 표 (docs/architecture.md, v2 architecture-review 15장): 자리 하나가 한 줄이다. 배열 번호가 곧 통신 아이디(meet의 seat, 서버 Player.seat)라서
// 한 번 정한 번호는 바꾸지 않는다. 번호가 바뀌면 test/seats.mjs가 test/seats-snapshot.json과 달라서 npm run check가 실패한다. 새 자리는 배열 끝에 더한다.
// table = 테이블 번호: 같으면 같은 모임이다(영상·음성 연결 단위, 2단계). 장소를 가로질러 겹치지 않는다. pair = 학교 책상 짝 번호(책상 하나에 둘).
// x, y = 앉은 사람의 발 사이 점(px). face = 앉아서 보는 쪽(1 오른쪽, -1 왼쪽). back = 뒷줄 자리(앞 장식 층 뒤에 그린다)
export type Seat = { room: RoomId; table: number; x: number; y: number; face: 1 | -1; back?: true; pair?: number; teacher?: true };
// 테이블 하나: 그림을 놓는 기준 x(js/town.js가 책상·테이블을 그리는 자리)와 종류. 종류는 그림 모양만 정한다
export type TableKind = 'bench' | 'pair' | 'meeting' | 'date' | 'lectern';
export type Table = { table: number; room: RoomId; kind: TableKind; id: string; x: number };

// 테이블 번호: 0 마을 벤치, 1~6 학교 책상 여섯(짝 0~5), 7·8 퍼스트파이브 회의 테이블 A·B, 9·10 스타박스 둘이 앉는 테이블, 11 학교 교탁(강사 자리 하나)
export const TABLES: Table[] = [
  { table: 0, room: 'town', kind: 'bench', id: 'bench', x: 457 },
  ...[120, 160, 200, 240, 280, 320].map((x, i): Table => ({ table: 1 + i, room: 'school', kind: 'pair', id: 'desk' + (i + 1), x })),
  { table: 7, room: 'office', kind: 'meeting', id: 'A', x: 170 },
  { table: 8, room: 'office', kind: 'meeting', id: 'B', x: 316 },
  { table: 9, room: 'starbox', kind: 'date', id: 't1', x: 96 },
  { table: 10, room: 'starbox', kind: 'date', id: 't2', x: 176 },
  { table: 11, room: 'school', kind: 'lectern', id: 'lectern', x: 468 },
];

// 긴 회의 테이블: 뒤에 셋, 앞에 셋을 어긋나게 놓아 여섯이 모두 보이게 한다
const meeting = (t: Table): Seat[] => [12, 24, 36, 48, 60, 72].map((dx, i) => ({ room: t.room, table: t.table, x: t.x + dx, y: i % 2 ? 178 : 164, face: dx < 42 ? 1 : -1, ...(i % 2 ? {} : { back: true as const }) }));
// 학교 책상: 뒷줄 하나, 앞줄 하나. 둘 다 칠판(오른쪽)을 본다
const pair = (t: Table): Seat[] => [{ room: t.room, table: t.table, x: t.x, y: 164, face: 1, back: true, pair: t.table - 1 }, { room: t.room, table: t.table, x: t.x + 14, y: 178, face: 1, pair: t.table - 1 }];
// 스타박스: 작은 테이블 하나에 의자 둘
const date = (t: Table): Seat[] => [{ room: t.room, table: t.table, x: t.x - 16, y: 172, face: 1 }, { room: t.room, table: t.table, x: t.x + 16, y: 172, face: -1 }];

// 교탁: 칠판 앞 한 자리. 앉으면 강사다(docs 9장). 교탁 그림이 앉은 사람의 아랫몸을 가리도록 뒷줄로 놓고, 학생들 쪽(왼쪽)을 본다
const lectern = (t: Table): Seat[] => [{ room: t.room, table: t.table, x: t.x, y: 164, face: -1, back: true, teacher: true }];

// 번호: 0·1 마을 벤치, 2~13 학교(책상 순서대로 뒷줄·앞줄), 14~25 퍼스트파이브(A 14~19, B 20~25), 26~29 스타박스, 30 학교 교탁(강사)
export const SEATS: Seat[] = [
  { room: 'town', table: 0, x: 452, y: 175, face: 1 },
  { room: 'town', table: 0, x: 462, y: 175, face: -1 },
  ...TABLES.filter((t) => t.kind === 'pair').flatMap(pair),
  ...TABLES.filter((t) => t.kind === 'meeting').flatMap(meeting),
  ...TABLES.filter((t) => t.kind === 'date').flatMap(date),
  ...TABLES.filter((t) => t.kind === 'lectern').flatMap(lectern),
];

// ---- 표에서 나오는 값 (고치지 말고 표를 고친다)
export const MAX_SEAT = SEATS.length - 1;   // 자리 번호 상한 (서버가 받는 seat의 범위)
export const TEACHER_SEAT = SEATS.findIndex((s) => s.teacher);   // 강사 자리 번호 (교탁)
export const tableOf = (seat: number) => (seat >= 0 && SEATS[seat] ? SEATS[seat].table : -1);
export const pairOf = (seat: number) => (seat >= 0 && SEATS[seat] && SEATS[seat].pair !== undefined ? SEATS[seat].pair! : -1);
export const seatsIn = (room: RoomId) => SEATS.map((s, id) => ({ ...s, id })).filter((s) => s.room === room);
export const tablesIn = (room: RoomId) => TABLES.filter((t) => t.room === room);
